import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { decode } from 'html-entities';

import Form from '@components/library/form/Form';
import InputContainer from '@components/library/input_container/InputContainer';
import renderModal from '@components/show_page/modals/renderModal';
import { create as submitRequest } from '@api/v4/resources/download_requests';
import { pluralizeObject } from '@helpers/show_page_helpers';

const RequestDownloadAccessModal = ({
  closeModal,
  selectedAssetKeys,
  selectedViewOnlyAssetKeys
}) => {
  const [formDisabled, setFormDisabled] = useState(false);

  useEffect(() => (() => closeModal()), []);

  const assetQtyObject = pluralizeObject(selectedAssetKeys);
  let resourceInfo;

  if (BFG.manifest) {
    resourceInfo = `${bfTranslate('the')} "${BFG.manifest.name}" ${bfTranslate('Share Link')}`;
  } else if (BFG.resource.type === 'collection') {
    resourceInfo = `${bfTranslate('the')} "${decode(BFG.resource.name)}" ${bfTranslate('Collection')}`;
  } else {
    resourceInfo = `"${decode(BFG.resource.name)}"`;
  }

  const successOptions = () => ({
    type: 'success',
    title: bfTranslate('Success!'),
    text: `${bfTranslate(`You have requested access to download ${assetQtyObject.qty} asset${assetQtyObject.plural} from`)} ${resourceInfo}`,
    showCancelButton: true,
    showConfirmButton: false,
    cancelButtonText: bfTranslate('Close'),
    allowOutsideClick: true,
    customClass: 'swal-request-download-confirmation',
  });

  const getAssetKeysToSubmit = () => {
    const viewOnlyAssetsEnabled = BFG.hasFeature('view_only_assets');

    // if a resource that's NOT view only, only assets marked as view only are request download
    if (viewOnlyAssetsEnabled && !BFG.viewOnly) {
      return Array.from(selectedViewOnlyAssetKeys);
    } else {
      // if resource that's view only, all assets are request download
      return Array.from(selectedAssetKeys);
    }
  }

  const handleSubmit = (inputs) => {
    setFormDisabled(true);
    let key;
    let type;

    if (BFG.manifest) {
      ({ key, type } = BFG.manifest);
    } else {
      ({ key, type } = BFG.resource);
    }

    const inputValues = {};
    Object.keys(inputs).forEach((input) => { inputValues[input] = inputs[input].value; });

    submitRequest({
      key,
      type,
      assetKeys: getAssetKeysToSubmit(),
      ...inputValues
    })
      .then(() => {
        window.swal(successOptions());
      })
      .catch((err) => {
        setFormDisabled(false);
        console.log(err);
      });
  };

  return (
    <div className="modal-content-wrapper request-download-modal">
      <div className="modal-content-wrapper__header">
        <span aria-hidden="true" className="bff-request-download icon" />
        <h3 className="modal-title">{bfTranslate('Request Download')}</h3>
        <button
          className="close-button"
          onClick={closeModal}
          type="button"
        >
          <span className="bff-close" />
        </button>
      </div>
      <div className="modal-content-wrapper__body">
        <div>
          <h3 className="body-title">
            {bfTranslate('Provide your contact information and intended usage for the selected assets.')}
          </h3>
          <Form
            disabledByParent={formDisabled}
            onSubmit={handleSubmit}
          >
            <InputContainer
              attributes={{
                name: 'name',
                type: 'text',
              }}
              labelCopy={bfTranslate('Name')}
            />
            <InputContainer
              attributes={{
                name: 'email-address',
                type: 'email',
              }}
              labelCopy={bfTranslate('Email Address')}
            />
            <InputContainer
              attributes={{
                name: 'company',
                type: 'text',
              }}
              labelCopy={bfTranslate('Company')}
            />
            <InputContainer
              attributes={{
                name: 'usage-details',
                type: 'textarea',
              }}
              labelCopy={bfTranslate('Usage Details')}
            />
          </Form>
        </div>
      </div>
    </div>
  );
};

RequestDownloadAccessModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
  selectedViewOnlyAssetKeys: PropTypes.instanceOf(Set).isRequired
};

const ModalComponent = renderModal(RequestDownloadAccessModal, 'request-download-access-modal');
export default ModalComponent;
